import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  Bell,
  ChevronRight,
  Settings,
  Sidebar as SidebarIcon,
  MessageCircle,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';

const pathNames = {
  '/dashboard':           'Dashboard',
  '/grammar':             'Libri i Gramatikës',
  '/exam/b2':             'Provimi Goethe B2',
  '/exam/b2/lesen':       'Lesen B2',
  '/exam/b2/hoeren':      'Hören B2',
  '/exam/b2/schreiben':   'Schreiben B2',
  '/exam/b2/sprechen':    'Sprechen B2',
  '/vocabulary':          'Fjalor',
  '/unknown-words':       'Fjalët e Ruajtura',
  '/profession':          'Profesioni',
  '/typing-practice':     'Shtypi',
  '/timed-challenge':     'Sfida e Kohës',
  '/journal':             'Ditari',
  '/bookmarks':           'Të Ruajturat',
  '/settings':            'Cilësimet',
  '/chat':                'Biseda',
  '/admin':               'Admin Console',
};

const formatNotificationTime = (timestamp, language) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(language === 'de' ? 'de-DE' : 'sq-AL', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const Topbar = ({ onMenuClick, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [notificationFilter, setNotificationFilter] = useState('all');
  const notificationPanelRef = useRef(null);

  const currentPage = t(pathNames[location.pathname] || 'Dashboard');
  const isDashboard = location.pathname === '/dashboard';

  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }
    setNotificationsLoading(true);
    setNotificationsError('');
    try {
      const { data: memberships, error: membershipsError } = await supabase
        .from('conversation_members')
        .select('conversation_id')
        .eq('user_id', user.id);
      if (membershipsError) throw membershipsError;
      const conversationIds = [...new Set((memberships || []).map((row) => row.conversation_id))];
      if (!conversationIds.length) {
        setNotifications([]);
        return;
      }

      const [conversationResult, messageResult] = await Promise.all([
        supabase.from('conversations').select('id, kind, name').in('id', conversationIds),
        supabase.from('messages')
          .select('id, conversation_id, sender_id, sender_name, content, timestamp, message_type')
          .in('conversation_id', conversationIds)
          .neq('sender_id', user.id)
          .order('timestamp', { ascending: false })
          .limit(100),
      ]);
      if (conversationResult.error) throw conversationResult.error;
      if (messageResult.error) throw messageResult.error;

      const incomingMessages = messageResult.data || [];
      if (!incomingMessages.length) {
        setNotifications([]);
        return;
      }
      const messageIds = incomingMessages.map((message) => message.id);
      const senderIds = [...new Set(incomingMessages.map((message) => message.sender_id))];
      const [readResult, profileResult, hiddenResult] = await Promise.all([
        supabase.from('message_reads')
          .select('message_id')
          .eq('user_id', user.id)
          .in('message_id', messageIds),
        supabase.from('profiles')
          .select('id, username, first_name, last_name, avatar_url')
          .in('id', senderIds),
        supabase.from('message_hides')
          .select('message_id, hide_at')
          .eq('user_id', user.id)
          .in('message_id', messageIds),
      ]);
      if (readResult.error) throw readResult.error;
      if (profileResult.error) throw profileResult.error;
      if (hiddenResult.error) throw hiddenResult.error;

      const readIds = new Set((readResult.data || []).map((row) => row.message_id));
      const now = Date.now();
      const hiddenIds = new Set((hiddenResult.data || [])
        .filter((row) => !row.hide_at || new Date(row.hide_at).getTime() <= now)
        .map((row) => row.message_id));
      const conversationsById = new Map((conversationResult.data || []).map((row) => [row.id, row]));
      const profilesById = new Map((profileResult.data || []).map((profile) => [profile.id, profile]));
      setNotifications(incomingMessages.filter((message) => !hiddenIds.has(message.id)).map((message) => {
        const conversation = conversationsById.get(message.conversation_id);
        const profile = profilesById.get(message.sender_id);
        const senderName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
          || profile?.username
          || message.sender_name
          || 'Mesazh i ri';
        return {
          ...message,
          isRead: readIds.has(message.id),
          senderName,
          avatarUrl: profile?.avatar_url,
          conversationName: conversation?.kind === 'group' ? conversation.name : null,
        };
      }));
    } catch (error) {
      console.error('Unable to load notifications:', error);
      setNotificationsError(t('Njoftimet nuk u ngarkuan. Provo përsëri.'));
    } finally {
      setNotificationsLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => {
    if (!user?.id) return undefined;
    loadNotifications();
    const channel = supabase.channel(`topbar-notifications:${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        loadNotifications();
        const message = payload.new;
        if (
          message?.sender_id !== user.id
          && localStorage.getItem('app_desktop_notifications') === 'true'
          && 'Notification' in window
          && Notification.permission === 'granted'
        ) {
          try {
            const notification = new Notification(message.sender_name || 'Mesazh i ri', {
              body: (message.content || 'Dërgoi një bashkëngjitje.').slice(0, 120),
              tag: `chat-message-${message.id}`,
            });
            notification.onclick = () => {
              window.focus();
              navigate('/chat', { state: { chatConversationId: message.conversation_id } });
              notification.close();
            };
          } catch (error) {
            console.error('Unable to show desktop notification:', error);
          }
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reads' }, loadNotifications)
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Unable to subscribe to chat notifications:', error || status);
        }
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadNotifications, navigate, user?.id]);

  useEffect(() => {
    if (!notificationsOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [notificationsOpen]);

  const openNotification = async (notification) => {
    const { error } = await supabase.from('message_reads').upsert(
      { message_id: notification.id, user_id: user.id },
      { onConflict: 'message_id,user_id' }
    );
    if (error) {
      console.error('Unable to mark notification as read:', error);
      setNotificationsError(t('Njoftimi nuk u hap. Provo përsëri.'));
      return;
    }
    setNotifications((previous) => previous.map((item) => (
      item.id === notification.id ? { ...item, isRead: true } : item
    )));
    setNotificationsOpen(false);
    navigate('/chat', { state: { chatConversationId: notification.conversation_id } });
  };

  const markAllNotificationsRead = async () => {
    const unreadNotifications = notifications.filter((notification) => !notification.isRead);
    if (!unreadNotifications.length) return;
    const { error } = await supabase.from('message_reads').upsert(
      unreadNotifications.map((notification) => ({ message_id: notification.id, user_id: user.id })),
      { onConflict: 'message_id,user_id' }
    );
    if (error) {
      console.error('Unable to mark all chat notifications as read:', error);
      setNotificationsError('Njoftimet nuk u shënuan si të lexuara. Provo përsëri.');
      return;
    }
    setNotifications((previous) => previous.map((notification) => ({ ...notification, isRead: true })));
    setNotificationsError('');
  };

  const unreadCount = notifications.filter((notification) => !notification.isRead).length;
  const visibleNotifications = notificationFilter === 'unread'
    ? notifications.filter((notification) => !notification.isRead)
    : notifications;

  return (
    <header className="bg-background border-b border-border h-14 flex items-center shrink-0 px-2 gap-2 sm:px-4 sm:gap-4 relative z-20 transition-colors">
      
      {/* Mobile menu trigger */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200 ease-in-out outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Desktop collapse trigger (matching entriks-hr SidebarTrigger) */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200 ease-in-out outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        title={isCollapsed ? "Zgjero sidebar" : "Palos sidebar"}
      >
        <SidebarIcon className="w-4 h-4" />
      </button>

      {/* Separator */}
      <div className="hidden lg:block w-px h-4 bg-border mx-1" />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Link to="/dashboard" className="hover:text-foreground transition-colors font-medium">
          Ballina
        </Link>
        {!isDashboard && (
          <>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            <span className="text-foreground font-semibold truncate max-w-[150px] sm:max-w-none">{currentPage}</span>
          </>
        )}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right actions */}
      <div className="flex items-center gap-1.5">
        {/* Notification bell */}
        <div className="relative" ref={notificationPanelRef}>
          <button
            type="button"
            onClick={() => {
              setNotificationsOpen((open) => !open);
              if (!notificationsOpen) loadNotifications();
            }}
            aria-label={`${t('Njoftimet')}${unreadCount ? `, ${unreadCount} të palexuara` : ''}`}
            aria-expanded={notificationsOpen}
            className="group relative rounded-lg p-2 text-muted-foreground outline-hidden transition-all duration-200 ease-in-out hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Bell className="h-5 w-5 transition-transform group-hover:scale-110" />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#DD0000] px-1 text-[9px] font-bold text-white ring-2 ring-background">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {notificationsOpen && (
            <section className="absolute right-0 top-full z-[100] mt-2 flex max-h-[min(75vh,32rem)] w-[min(32rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
              <div aria-hidden="true" className="flex h-1 shrink-0">
                <span className="flex-1 bg-zinc-950" />
                <span className="flex-1 bg-[#DD0000]" />
                <span className="flex-1 bg-[#FFCE00]" />
              </div>
              <header className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
                <div>
                  <h2 className="text-sm font-extrabold text-zinc-950">{t('Njoftimet')}</h2>
                  <p className="text-[11px] text-zinc-500">
                    {t('Njoftime për mesazhet e chat-it')}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Link
                    to="/settings"
                    state={{ activeTab: 'notifications' }}
                    onClick={() => setNotificationsOpen(false)}
                    aria-label={t('Hap cilësimet')}
                    className="rounded-lg p-1.5 text-zinc-500 transition hover:bg-zinc-100"
                  >
                    <Settings size={17} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setNotificationsOpen(false)}
                    aria-label={t('Mbyll njoftimet')}
                    className="rounded-lg p-1.5 text-zinc-500 transition hover:bg-zinc-100"
                  >
                    <X size={16} />
                  </button>
                </div>
              </header>
              <div className="flex items-center gap-1 border-b border-zinc-200 bg-zinc-50/80 px-4 py-2">
                {[
                  { id: 'all', label: t('Të gjitha'), count: notifications.length },
                  { id: 'unread', label: t('Të palexuara'), count: unreadCount },
                ].map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setNotificationFilter(filter.id)}
                    aria-pressed={notificationFilter === filter.id}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      notificationFilter === filter.id
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                  >
                    {filter.label}
                    {filter.id === 'unread' && filter.count > 0 ? ` (${filter.count})` : ''}
                  </button>
                ))}
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllNotificationsRead}
                    className="ml-auto text-xs font-semibold text-[#9A6700] transition hover:text-zinc-900"
                  >
                    {t('Shëno të gjitha si të lexuara')}
                  </button>
                )}
              </div>
              <div className="min-h-24 flex-1 overflow-y-auto p-2">
                {notificationsLoading && notifications.length === 0 ? (
                  <p className="px-3 py-8 text-center text-xs text-zinc-500">{t('Duke ngarkuar njoftimet...')}</p>
                ) : notificationsError ? (
                  <div className="px-3 py-6 text-center">
                    <p role="alert" className="text-xs text-red-600">{notificationsError}</p>
                    <button type="button" onClick={loadNotifications} className="mt-2 text-xs font-bold text-[#DD0000]">{t('Provo përsëri')}</button>
                  </div>
                ) : visibleNotifications.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                    <MessageCircle size={22} className="text-zinc-300" />
                    <p className="text-sm font-semibold text-zinc-700">{t('Je në rregull')}</p>
                    <p className="max-w-sm text-xs text-zinc-400">
                      {t('Mesazhet e palexuara të chat-it do të shfaqen këtu.')}
                    </p>
                  </div>
                ) : visibleNotifications.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => openNotification(notification)}
                    className={`flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-amber-50/70 ${notification.isRead ? 'opacity-70' : ''}`}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-100 text-xs font-bold text-zinc-700">
                      {notification.avatarUrl
                        ? <img src={notification.avatarUrl} alt="" className="h-full w-full object-cover" />
                        : notification.senderName.slice(0, 2).toUpperCase()}
                    </div>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <strong className="truncate text-xs text-zinc-900">{notification.senderName}</strong>
                        <span className="shrink-0 text-[10px] text-zinc-400">
                          {formatNotificationTime(notification.timestamp, language)}
                        </span>
                      </span>
                      {notification.conversationName && <span className="mt-0.5 block truncate text-[10px] font-semibold text-[#9A6700]">{notification.conversationName}</span>}
                      <span className="mt-0.5 block truncate text-[11px] text-zinc-600">{notification.content || 'Dërgoi një bashkëngjitje'}</span>
                    </span>
                    {!notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#DD0000]" />}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="w-px h-6 bg-border mx-1 hidden sm:block" />

      </div>
    </header>
  );
};

export default Topbar;
